import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { dataSource } from "@/lib/dataSource";
import { Type, Code, TypeEdge, AuditEntry, Settings } from "../shared/schema";

// Authentication
export function useAuthMe() {
  return useQuery({
    queryKey: ["authmetypes"],
    queryFn: () => dataSource.authMe(),
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: true,
  });
}

export function useRefresh() {
  return useQuery({
    queryKey: ["refreshtypes"],
    queryFn: () => dataSource.refresh(),
    enabled: false, // Don't auto-fetch, only call manually
    retry: false,
  });
}

export function useLogout() {
  return useMutation({
    mutationFn: () => dataSource.logout(),
    onSettled: () => {
      window.location.href = "https://bprm-ai-portal.workfinity.com/login";
    },
  });
}

export function useTypes(pageIndex: number, pageSize: number, typeKey?: string) {
  return useQuery({
    queryKey: ["types", pageIndex, pageSize, typeKey],
    queryFn: () => dataSource.listTypes(pageIndex, pageSize, typeKey),
  });
}

export function useChildTypeType(parentType: string, parentCode: string) {
  return useQuery({
    queryKey: ["types", parentType, parentCode],
    queryFn: () => dataSource.listChildType(parentType, parentCode),
    enabled: !!parentType,
    staleTime: 0,
  });
}

export function useGetFilterType(filterName?: string, filterType?: string) {
  return useQuery({
    queryKey: ["types", filterName, filterType],
    queryFn: () => dataSource.listFilterType(filterName ?? "", filterType ?? ""),
    placeholderData: keepPreviousData,
  });
}

export function useType(key: string) {
  return useQuery({
    queryKey: ["types", key],
    queryFn: () => dataSource.getType(key),
    enabled: !!key,
  });
}

export function useCodes(
  typeFilter?: string,
  parentTypeFilter?: string,
  parentCodeFilter?: string
) {
  return useQuery({
    queryKey: ["codes", typeFilter, parentTypeFilter, parentCodeFilter],
    queryFn: () =>
      dataSource.listCodes(typeFilter, parentTypeFilter, parentCodeFilter),
    // retry: 1,      
    // retryDelay: 1000,
  });
}

export function useCode(type: string, code: string) {
  return useQuery({
    queryKey: ["codes", type, code],
    queryFn: () => dataSource.getCode(type, code),
    enabled: !!type && !!code,
  });
}

export function useTypeEdges() {
  return useQuery({
    queryKey: ["typeEdges"],
    queryFn: () => dataSource.listTypeEdges(),
  });
}

export function useSearch(query: string) {
  return useQuery({
    queryKey: ["search", query],
    queryFn: () => dataSource.search(query),
    enabled: query.length >= 2,
  });
}

export function useDataQuality() {
  return useQuery({
    queryKey: ["dataQuality"],
    queryFn: () => dataSource.analyzeDataQuality(),
  });
}

export function useAuditLog(filters?: any) {
  return useQuery({
    queryKey: ["auditLog", filters],
    queryFn: () => dataSource.getAuditLog(filters),
  });
}

export function useStats() {
  return useQuery({
    queryKey: ["stats"],
    queryFn: () => dataSource.getStats(),
  });
}

// AI Assistant
export function useChatWithAI() {
  return useMutation({
    mutationFn: ({ message, sessionId, microservice = "codemanager", page = 1 }: { message: string; sessionId: string; microservice?: string; page?: number }) => 
      dataSource.chatWithAI(message, sessionId, microservice, page),
  });
}

// Mutations
export function useCreateType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: dataSource.createType.bind(dataSource),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["types"] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
    },
  });
}

export function useUpdateType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, updates }: { key: string; updates: Partial<Type> }) =>
      dataSource.updateType(key, updates),
    onSuccess: (_, { key }) => {
      queryClient.invalidateQueries({ queryKey: ["types"] });
      queryClient.invalidateQueries({ queryKey: ["types", key] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
    },
  });
}

export function useDeleteType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, force }: { key: string; force?: boolean; }) =>
      dataSource.deleteType(key, force),
    onMutate: async ({ key }) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: ["types"] }),
        queryClient.cancelQueries({ queryKey: ["codes"] }),
      ]);
      const previousTypes = queryClient.getQueryData<any>(["types"]);
      const previousCodes = queryClient.getQueryData<any>(["codes"]);
      if (previousTypes) {
        queryClient.setQueryData(["types"], (old: any) =>
          (old || []).filter((t: any) => t.key !== key)
        );
      }
      if (previousCodes) {
        queryClient.setQueryData(["codes"], (old: any) =>
          (old || []).filter((c: any) => c.type !== key)
        );
      }
      return { previousTypes, previousCodes } as const;
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      if (ctx.previousTypes)
        queryClient.setQueryData(["types"], ctx.previousTypes);
      if (ctx.previousCodes)
        queryClient.setQueryData(["codes"], ctx.previousCodes);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["types"] });
      queryClient.invalidateQueries({ queryKey: ["codes"] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
    },
  });
}

export function useCreateCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: dataSource.createCode.bind(dataSource),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["codes"] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
    },
  });
}

export function useUpdateCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      type,
      code,
      updates,
    }: {
      type: string;
      code: string;
      updates: Partial<Code>;
    }) => dataSource.updateCode(type, code, updates),
    onSuccess: (_, { type, code }) => {
      queryClient.invalidateQueries({ queryKey: ["codes"] });
      queryClient.invalidateQueries({ queryKey: ["codes", type, code] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
    },
  });
}

export function useDeleteCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      type,
      code,
      force,
    }: {
      type: string;
      code: string;
      force?: boolean;
    }) => dataSource.deleteCode(type, code, force),
    onMutate: async ({ type, code }) => {
      await queryClient.cancelQueries({ queryKey: ["codes"] });
      const previousCodes = queryClient.getQueryData<any>(["codes"]);
      if (previousCodes) {
        queryClient.setQueryData(["codes"], (old: any) =>
          (old || []).filter((c: any) => !(c.type === type && c.code === code))
        );
      }
      return { previousCodes } as const;
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previousCodes)
        queryClient.setQueryData(["codes"], ctx.previousCodes);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["codes"] });
      queryClient.invalidateQueries({ queryKey: ["types"] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
    },
  });
}
